import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";

// Custom marker: reuses the same 3D "house" nav-icon rendered across the app
// (top bar and boards list), so the community map speaks the same visual
// language. The count badge uses the app's mono-num type stack — same as the
// number pills on cards and stats — for typographic consistency.
const folderIcon = (count) => L.divIcon({
  className: "",
  html: `
    <div style="position:relative;transform:translate(-50%,-100%);width:44px;height:44px;">
      <div style="width:44px;height:44px;border-radius:14px;background:#ffffff;border:1.5px solid rgba(15,23,42,0.12);box-shadow:0 6px 14px -4px rgba(15,23,42,0.35), 0 2px 4px rgba(15,23,42,0.15);display:flex;align-items:center;justify-content:center;">
        <img src="/nav-icons/house.png" alt="scheda" style="width:32px;height:32px;object-fit:contain;pointer-events:none;user-select:none;" />
      </div>
      <span style="position:absolute;top:-6px;right:-6px;min-width:20px;height:20px;padding:0 6px;background:#EF4444;color:#fff;font-family:'SF Mono','JetBrains Mono',monospace;font-variant-numeric:tabular-nums;letter-spacing:-0.01em;font-size:11px;font-weight:700;border-radius:999px;border:2px solid #fff;line-height:16px;text-align:center;box-shadow:0 2px 4px rgba(0,0,0,0.2);">${count || 0}</span>
    </div>`,
  iconSize: [44, 44], iconAnchor: [22, 44],
});

// Fit the map to all markers whenever the boards list changes; falls back
// to a stable Italy centre when nothing is geocoded yet.
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) { map.setView([42.6, 12.5], 5); return; }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds.pad(0.25), { animate: true, maxZoom: 8 });
  }, [points, map]);
  return null;
};

const CommunityBoardMap = ({ communityId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!communityId) return;
    (async () => {
      setLoading(true);
      try { const r = await api.get(`/communities/${communityId}/boards-geo`); setItems(r.data || []); }
      catch { setItems([]); }
      finally { setLoading(false); }
    })();
  }, [communityId]);

  // Cluster: group markers whose centres are within ~15km (~0.15° lat/lon).
  // The one point per cluster is placed at the centroid; the icon badge
  // aggregates the property counts so users instantly see the density.
  const clustered = useMemo(() => {
    const CELL = 0.15;
    const buckets = new Map();
    for (const it of items) {
      const key = `${Math.round(it.lat / CELL)}:${Math.round(it.lon / CELL)}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(it);
    }
    return [...buckets.values()].map((grp) => {
      if (grp.length === 1) return grp[0];
      const lat = grp.reduce((a, b) => a + b.lat, 0) / grp.length;
      const lon = grp.reduce((a, b) => a + b.lon, 0) / grp.length;
      const count = grp.reduce((a, b) => a + (b.count || 0), 0);
      return { lat, lon, count, cluster: true, group: grp, name: `${grp.length} schede` };
    });
  }, [items]);

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 relative" data-testid="community-map">
      <div className="h-64 relative">
        <MapContainer
          center={[42.6, 12.5]} zoom={5} scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={items} />
          {clustered.map((p, i) => (
            <Marker key={i} position={[p.lat, p.lon]} icon={folderIcon(p.count)}>
              <Popup>
                {p.cluster ? (
                  <div className="text-xs">
                    <div className="font-semibold mb-1">{p.group.length} schede in zona</div>
                    <ul className="space-y-0.5">
                      {p.group.slice(0, 8).map((g) => <li key={g.board_id}>· {g.name} <span className="text-slate-500">({g.count})</span></li>)}
                      {p.group.length > 8 && <li className="text-slate-500">…e altre {p.group.length - 8}</li>}
                    </ul>
                  </div>
                ) : (
                  <div className="text-xs">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-slate-500 mt-0.5">{p.count} immobili geocodati</div>
                  </div>
                )}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {!loading && items.length === 0 && (
        <div className="absolute inset-0 bg-white/85 flex items-center justify-center text-sm text-slate-500 pointer-events-none">
          Nessuna scheda geolocalizzata dai membri.
        </div>
      )}
    </div>
  );
};

export default CommunityBoardMap;
